import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, Button, Alert, Empty, Modal, Form, Select, Input, Tooltip } from 'antd';
import { ReloadOutlined, TeamOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import mobileService from '../../services/mobileService';
import { formatDateHeure, can, HAB } from '../../variables/constants';
import i18n from '../../i18n';
import { avecLibelleTraduit } from '../../i18n/libelles';

const MUTED = '#6b7280';

/**
 * Comptes de l'application mobile, vus par un agent.
 *
 * <h3>Ce que le controle d'identite autorise</h3>
 * `identiteVerifiee` n'est pas une mention decorative : c'est elle qui autorise
 * l'usager a poser des gestes ENGAGEANTS - accepter ou refuser une offre
 * d'indemnisation. Un compte non verifie peut consulter, pas s'engager. La
 * colonne est donc la premiere information apres l'identite, et l'action porte
 * ce nom plutot qu'un vague « Modifier ».
 *
 * L'acte est REVERSIBLE, contrairement au rattachement : repasser un compte en
 * non verifie retire l'autorisation sans effacer la piece deja enregistree, de
 * facon a garder trace de ce qui avait ete presente.
 *
 * <h3>Ce que cet ecran ne montre pas</h3>
 * Ni empreinte de mot de passe, ni compteur d'echecs, ni date de blocage : le
 * backend ne les expose pas, deliberement. Ils servent la defense du compte, et
 * un agent n'en a besoin pour aucune de ses taches. Ne pas chercher a les
 * afficher.
 */

const STATUT_COMPTE = avecLibelleTraduit({
  EN_ATTENTE_VERIFICATION: { color: 'orange' },
  ACTIF: { color: 'green' },
  BLOQUE: { color: 'red' },
  SUPPRIME: { color: 'default' },
}, 'mobile:comptes.statuts');

const PIECES = avecLibelleTraduit([
  { value: 'CNI' },
  { value: 'PASSEPORT' },
  { value: 'PERMIS_CONDUIRE' },
  { value: 'CARTE_SEJOUR' },
  { value: 'AUTRE' },
], 'mobile:comptes.pieces');

const libellePiece = (v) => PIECES.find((p) => p.value === v)?.label || v;

export default function ComptesUsagers() {
  const { t } = useTranslation('mobile');
  const [lignes, setLignes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [controle, setControle] = useState({ open: false, usager: null });
  const [enCours, setEnCours] = useState(false);
  const [form] = Form.useForm();

  const { current, pageSize } = pagination;
  const peutLire = can(HAB.MOBILE_USAGER_READ);
  const peutVerifier = can(HAB.MOBILE_USAGER_VERIFIER_IDENTITE);

  const charger = useCallback(async (page, taille) => {
    setChargement(true);
    setErreur(null);
    try {
      const res = await mobileService.usagers({ page, size: taille });
      setLignes(res.items);
      setPagination((p) => ({ ...p, current: res.page, pageSize: res.pageSize, total: res.total }));
    } catch (err) {
      setErreur(err?.response?.data?.message || i18n.t('mobile:comptes.erreurChargement'));
      setLignes([]);
      setPagination((p) => ({ ...p, total: 0 }));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    if (peutLire) charger(current, pageSize);
    else setChargement(false);
  }, [charger, current, pageSize, peutLire]);

  const ouvrirControle = (u) => {
    form.setFieldsValue({
      verifiee: u.identiteVerifiee,
      pieceType: u.pieceType || undefined,
      pieceNumero: undefined,
    });
    setControle({ open: true, usager: u });
  };

  const enregistrer = async () => {
    const vals = await form.validateFields();
    setEnCours(true);
    try {
      const maj = await mobileService.verifierIdentite(controle.usager.usagerTrackingId, {
        verifiee: vals.verifiee,
        pieceType: vals.pieceType || null,
        pieceNumero: vals.pieceNumero?.trim() || null,
      });
      toast.success(vals.verifiee ? t('comptes.identiteValidee') : t('comptes.validationRetiree'));
      // On remplace la ligne avec ce que le backend renvoie plutot que de recharger :
      // l'agent traite souvent plusieurs comptes a la suite, et un rechargement
      // complet lui ferait perdre sa position dans la liste.
      if (maj) {
        setLignes((prev) => prev.map((l) =>
          l.usagerTrackingId === maj.usagerTrackingId ? maj : l));
      } else {
        charger(current, pageSize);
      }
      setControle({ open: false, usager: null });
    } catch (err) {
      toast.error(err?.response?.data?.message || t('comptes.enregistrementImpossible'));
    } finally {
      setEnCours(false);
    }
  };

  const colonnes = [
    {
      title: t('comptes.colonnes.usager'),
      key: 'usager',
      width: 250,
      render: (_, u) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 650 }}>
            {[u.prenom, u.nom].filter(Boolean).join(' ') || '-'}
          </div>
          <div style={{
            fontSize: 12, color: MUTED,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {u.email || '-'}
          </div>
          <div style={{ fontSize: 12, color: MUTED }}>{u.telephone || '-'}</div>
        </div>
      ),
    },
    {
      title: t('comptes.colonnes.identite'),
      dataIndex: 'identiteVerifiee',
      width: 200,
      render: (v, u) => (
        <div>
          <Tooltip title={v
            ? t('comptes.aideVerifiee')
            : t('comptes.aideNonVerifiee')}>
            <Tag color={v ? 'green' : 'orange'} style={{ borderRadius: 12, marginInlineEnd: 0, fontWeight: 600 }}>
              {v ? t('comptes.verifiee') : t('comptes.nonVerifiee')}
            </Tag>
          </Tooltip>
          {u.pieceType && (
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 3 }}>
              {libellePiece(u.pieceType)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('comptes.colonnes.compte'),
      dataIndex: 'statutCompte',
      width: 150,
      render: (v) => {
        const s = STATUT_COMPTE[v];
        return s
          ? <Tag color={s.color} style={{ borderRadius: 12, marginInlineEnd: 0 }}>{s.label}</Tag>
          : <span style={{ color: '#9ca3af' }}>{v || '-'}</span>;
      },
    },
    {
      title: t('comptes.colonnes.langue'),
      dataIndex: 'langue',
      width: 90,
      render: (v) => <span style={{ fontSize: 12.5, color: MUTED }}>{v || '-'}</span>,
    },
    {
      title: t('comptes.colonnes.inscritLe'),
      dataIndex: 'dateCreation',
      width: 160,
      render: (v) => <span style={{ fontSize: 12.5 }}>{v ? formatDateHeure(v) : '-'}</span>,
    },
    {
      title: '',
      key: 'actions',
      width: 190,
      align: 'right',
      render: (_, u) => (
        <Button
          size="small"
          icon={<SafetyCertificateOutlined />}
          disabled={!peutVerifier}
          onClick={() => ouvrirControle(u)}
        >
          {t('comptes.controlerIdentite')}
        </Button>
      ),
    },
  ];

  if (!peutLire) {
    return (
      <Layout title={t('comptes.titre')}>
        <Alert type="warning" showIcon
          message={t('comptes.reserveLecture')} />
      </Layout>
    );
  }

  return (
    <Layout title={t('comptes.titre')}>
      <PageHeader
        titre={t('comptes.titre')}
        icone={<TeamOutlined />}
        compte={chargement ? undefined : pagination.total}
        unite={t('comptes.unite')}
        actions={
          <Button icon={<ReloadOutlined />} onClick={() => charger(current, pageSize)}>
            {t('comptes.actualiser')}
          </Button>
        }
      />

      {!peutVerifier && (
        <Alert
          type="info"
          showIcon
          message={t('comptes.consultationSeule')}
          style={{ marginBottom: 16 }}
        />
      )}

      {erreur && (
        <Alert type="error" showIcon message={erreur} style={{ marginBottom: 16 }}
          action={<Button size="small" onClick={() => charger(current, pageSize)}>{t('comptes.reessayer')}</Button>} />
      )}

      <Table
        className="cb-table"
        rowKey="usagerTrackingId"
        dataSource={lignes}
        columns={colonnes}
        loading={chargement}
        size="middle"
        scroll={{ x: 1040 }}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('comptes.aucunCompte')} />,
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (n) => t('comptes.total', { count: n }),
          onChange: (page, taille) => setPagination((p) => ({ ...p, current: page, pageSize: taille })),
        }}
      />

      <Modal
        open={controle.open}
        title={t('comptes.modale.titre', { nom: [controle.usager?.prenom, controle.usager?.nom].filter(Boolean).join(' ') })}
        okText={t('comptes.modale.enregistrer')}
        cancelText={t('comptes.modale.annuler')}
        confirmLoading={enCours}
        okButtonProps={{ style: { background: '#1a6b3c', borderColor: '#1a6b3c' } }}
        onOk={enregistrer}
        onCancel={() => setControle({ open: false, usager: null })}
        destroyOnClose
      >
        <p style={{ color: MUTED, fontSize: 12.5, marginTop: 0 }}>
          {t('comptes.modale.explication')}
        </p>
        <Form form={form} layout="vertical">
          <Form.Item
            name="verifiee"
            label={t('comptes.modale.resultat')}
            rules={[{ required: true, message: t('comptes.modale.resultatObligatoire') }]}
          >
            <Select
              options={[
                { value: true, label: t('comptes.modale.identiteValidee') },
                { value: false, label: t('comptes.modale.nonValidee') },
              ]}
            />
          </Form.Item>
          <Form.Item name="pieceType" label={t('comptes.modale.piecePresentee')}>
            <Select allowClear options={PIECES} placeholder={t('comptes.modale.typePiece')} />
          </Form.Item>
          <Form.Item
            name="pieceNumero"
            label={t('comptes.modale.numeroPiece')}
            rules={[{ max: 60, message: t('comptes.modale.max60') }]}
          >
            <Input placeholder={t('comptes.modale.telQuInscrit')} />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
